import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";
import FileUpload from "../../components/FileUpload";
import { adminApi } from "../../services/adminApi";

const TABS = ["Overview", "Profile", "Documents", "Signed Docs", "Insurance Card", "Messages"];
const DOC_CATEGORIES = [
  "driver_license", "medical_certificate", "background_check",
  "fingerprint_clearance", "mvr", "wc43", "vehicle_registration", "vehicle_inspection",
  "vehicle_maintenance", "airport_badge", "other",
];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const STATUS_COLORS = {
  active: "bg-green-100 text-green-700 border-green-200",
  pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
  inactive: "bg-gray-100 text-gray-500 border-gray-200",
};
const ENROLL_COLORS = {
  completed: "bg-green-100 text-green-700",
  in_progress: "bg-blue-100 text-blue-700",
  not_started: "bg-gray-100 text-gray-500",
};
const EXPIRY_CHIP = {
  expired: "bg-red-100 text-red-700 border-red-200",
  expiring_soon: "bg-yellow-100 text-yellow-700 border-yellow-200",
  valid: "bg-green-100 text-green-700 border-green-200",
  no_expiry: "bg-gray-100 text-gray-500 border-gray-200",
};

function expiryStatus(doc) {
  if (!doc.expiryDate) return "no_expiry";
  const days = Math.ceil((new Date(doc.expiryDate) - new Date()) / 86400000);
  if (days < 0) return "expired";
  if (days <= 30) return "expiring_soon";
  return "valid";
}

function SignedDocsTab({ signed, driverId }) {
  const [downloading, setDownloading] = useState(null);
  const [dlError, setDlError] = useState("");

  async function handleDownload(s) {
    setDownloading(s.documentId);
    setDlError("");
    try {
      const { link } = await adminApi.getSignedDocLink(driverId, s.documentId);
      window.open(link, "_blank");
    } catch {
      setDlError(`Could not download "${s.documentLabel}".`);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-900">Documents Signed by Driver</h2>
        <p className="text-xs text-gray-400 mt-0.5">Full signed documents stored in Dropbox — includes original content + driver signature.</p>
      </div>
      {dlError && <p className="px-5 py-2 text-sm text-red-600 bg-red-50 border-b border-red-100">{dlError}</p>}
      {signed.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-400 text-center">Driver has not signed any documents yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Document</th>
              <th className="px-4 py-2.5 text-left font-semibold">Signer Name</th>
              <th className="px-4 py-2.5 text-left font-semibold">Method</th>
              <th className="px-4 py-2.5 text-left font-semibold">Signed On</th>
              <th className="px-4 py-2.5 text-left font-semibold">File</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {signed.map((s) => (
              <tr key={s._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{s.documentLabel}</td>
                <td className="px-4 py-3 text-gray-600">{s.signerName}</td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">{s.signatureType}</span>
                </td>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(s.signedAt).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleDownload(s)}
                    disabled={downloading === s.documentId}
                    className="text-xs text-blue-600 hover:underline font-medium disabled:opacity-50"
                  >
                    {downloading === s.documentId ? "Opening…" : "Download PDF"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function DriverDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Overview edit
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState(false);

  // Profile edit
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  // Document upload
  const [docFile, setDocFile] = useState(null);
  const [docForm, setDocForm] = useState({ documentCategory: "driver_license", label: "", expiryDate: "" });
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Insurance card
  const [insFile, setInsFile] = useState(null);
  const [insExpiry, setInsExpiry] = useState("");
  const [uploadingIns, setUploadingIns] = useState(false);
  const [removingIns, setRemovingIns] = useState(false);
  const [insLink, setInsLink] = useState(null);

  // Document preview
  const [preview, setPreview] = useState(null); // { link, fileName, mimeType }

  // Confirm dialog
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm }

  // Verification letter
  const [dlLetter, setDlLetter] = useState(false);
  const [dlLetterError, setDlLetterError] = useState("");

  // AVI letter
  const [aviOpen, setAviOpen] = useState(false);
  const [aviType, setAviType] = useState("request_tag");
  const [aviPrefilling, setAviPrefilling] = useState(false);
  const [aviForm, setAviForm] = useState({ driverName: "", cabNumber: "", vin: "", make: "", model: "", year: "", licensePlate: "" });
  const [aviToForm, setAviToForm] = useState({ driverName: "", cabNumber: "", vin: "", make: "", model: "", year: "", licensePlate: "" });
  const [aviLookupCab, setAviLookupCab] = useState("");
  const [aviLookingUp, setAviLookingUp] = useState(false);
  const [aviGenerating, setAviGenerating] = useState(false);
  const [aviError, setAviError] = useState("");

  // Message compose
  const [composeBody, setComposeBody] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3500); };
  const err = (msg) => { setError(msg); setTimeout(() => setError(""), 5000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, vehicleRes] = await Promise.all([
        adminApi.getDriver(id),
        adminApi.listVehicles(),
      ]);
      setData(d);
      setVehicles(vehicleRes.vehicles || []);
      const v = d.driver;
      setEditForm({
        firstName: v.firstName, lastName: v.lastName,
        email: v.email, phone: v.phone,
        status: v.status || "pending",
        notes: v.notes || "",
      });
      const fmt = (d) => d ? new Date(d).toISOString().split("T")[0] : "";
      setProfileForm({
        dateOfBirth: fmt(v.dateOfBirth),
        address: { street: v.address?.street || "", city: v.address?.city || "", state: v.address?.state || "CO", zip: v.address?.zip || "" },
        licenseNumber: v.licenseNumber || "",
        licenseState: v.licenseState || "CO",
        licenseExpiry: fmt(v.licenseExpiry),
        licenseClass: v.licenseClass || "",
        dotMedicalNumber: v.dotMedicalNumber || "",
        dotMedicalExpiry: fmt(v.dotMedicalExpiry),
        llcName: v.llcName || "",
        ein: v.ein || "",
        cbiCheckDate: fmt(v.cbiCheckDate),
        cbiExpiryDate: fmt(v.cbiExpiryDate),
        emergencyContactName: v.emergencyContactName || "",
        emergencyContactPhone: v.emergencyContactPhone || "",
        emergencyContactRelationship: v.emergencyContactRelationship || "",
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      await adminApi.updateDriver(id, editForm);
      setEditMode(false);
      flash("Driver updated.");
      load();
    } catch (e) { err(e.message); }
    finally { setSaving(false); }
  }

  function handleResendWelcomeEmail() {
    setConfirm({
      title: "Resend EnrollMe login email?",
      message: `This will email the driver portal link and login credentials to ${data?.driver?.email}.`,
      confirmLabel: "Send Email",
      confirmClass: "bg-blue-600 hover:bg-blue-700",
      onConfirm: async () => {
        setConfirm(null);
        setResendingWelcome(true);
        try {
          const res = await adminApi.resendDriverWelcomeEmail(id);
          flash(res.message || "EnrollMe login email sent.");
        } catch (e) {
          err(e.message);
        } finally {
          setResendingWelcome(false);
        }
      },
    });
  }

  async function handleSaveProfile() {
    setSavingProfile(true);
    setError("");
    try {
      await adminApi.updateDriver(id, profileForm);
      setProfileEditMode(false);
      flash("Profile updated.");
      load();
    } catch (e) { err(e.message); }
    finally { setSavingProfile(false); }
  }

  async function handleUploadDoc(e) {
    e.preventDefault();
    if (!docFile) return;
    setUploadingDoc(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("documentCategory", docForm.documentCategory);
      fd.append("label", docForm.label || docForm.documentCategory.replace(/_/g, " "));
      if (docForm.expiryDate) fd.append("expiryDate", docForm.expiryDate);
      await adminApi.uploadDoc("driver", id, fd);
      setDocFile(null);
      setDocForm({ documentCategory: "driver_license", label: "", expiryDate: "" });
      flash("Document uploaded.");
      load();
    } catch (e) { err(e.message); }
    finally { setUploadingDoc(false); }
  }

  function handleDeleteDoc(docId) {
    setConfirm({
      title: "Delete document?",
      message: "This removes the file from storage. This cannot be undone.",
      onConfirm: async () => {
        setConfirm(null);
        try { await adminApi.deleteDoc(docId); flash("Document deleted."); load(); }
        catch (e) { err(e.message); }
      },
    });
  }

  async function handleUploadIns(e) {
    e.preventDefault();
    if (!insFile) return;
    setUploadingIns(true);
    try {
      const fd = new FormData();
      fd.append("file", insFile);
      if (insExpiry) fd.append("expiryDate", insExpiry);
      await adminApi.uploadInsuranceCard(id, fd);
      setInsFile(null);
      setInsLink(null);
      flash("Insurance card updated.");
      load();
    } catch (e) { err(e.message); }
    finally { setUploadingIns(false); }
  }

  async function loadInsLink() {
    try { const { link } = await adminApi.getInsuranceCardLink(id); setInsLink(link); }
    catch (e) { err(e.message); }
  }

  function handleDeleteInsuranceCard() {
    setConfirm({
      title: "Remove insurance card?",
      message: "This permanently removes the card from the driver portal and file storage. This cannot be undone.",
      confirmLabel: "Remove Card",
      onConfirm: async () => {
        setConfirm(null);
        setRemovingIns(true);
        try {
          await adminApi.deleteInsuranceCard(id);
          setInsLink(null);
          setInsFile(null);
          setInsExpiry("");
          flash("Insurance card removed.");
          await load();
        } catch (e) {
          err(e.message);
        } finally {
          setRemovingIns(false);
        }
      },
    });
  }

  async function handleSendMessage(e) {
    e.preventDefault();
    if (!composeSubject.trim() || !composeBody.trim()) return;
    setSendingMsg(true);
    try {
      await adminApi.sendMessage({
        subject: composeSubject.trim(),
        body: composeBody.trim(),
        recipientType: "individual",
        recipientIds: [id],
      });
      setComposeSubject("");
      setComposeBody("");
      flash("Message sent.");
      load();
    } catch (e) { err(e.message); }
    finally { setSendingMsg(false); }
  }

  function handleDeleteDriver() {
    setConfirm({
      title: `Delete ${data?.driver?.firstName} ${data?.driver?.lastName}?`,
      message: "This permanently removes the driver and all associated data. This cannot be undone.",
      confirmLabel: "Yes, Delete Driver",
      onConfirm: async () => {
        setConfirm(null);
        try { await adminApi.deleteDriver(id); navigate("/enrollme/admin/drivers"); }
        catch (e) { err(e.message); }
      },
    });
  }

  async function handleAviOpen() {
    const opening = !aviOpen;
    setAviOpen(opening);
    setAviError("");
    if (opening && !aviForm.driverName) {
      setAviPrefilling(true);
      try {
        const d = await adminApi.getAviPrefill(id);
        setAviForm(d);
      } catch (e) { /* silent — admin can fill manually */ }
      finally { setAviPrefilling(false); }
    }
  }

  async function handleAviLookup() {
    if (!aviLookupCab.trim()) return;
    setAviLookingUp(true);
    setAviError("");
    try {
      const d = await adminApi.getVehicleByCab(aviLookupCab.trim());
      setAviToForm(d);
    } catch (e) { setAviError(e.message || "Cab not found."); }
    finally { setAviLookingUp(false); }
  }

  async function handleAviGenerate() {
    setAviGenerating(true);
    setAviError("");
    try {
      await adminApi.generateAviLetter({
        type: aviType,
        ...aviForm,
        toCabNumber: aviToForm.cabNumber,
        toDriverName: aviToForm.driverName,
        toVin: aviToForm.vin,
        toMake: aviToForm.make,
        toModel: aviToForm.model,
        toYear: aviToForm.year,
        toLicensePlate: aviToForm.licensePlate,
      });
    } catch (e) { setAviError(e.message || "Failed to generate letter."); }
    finally { setAviGenerating(false); }
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;
  if (!data) return <div className="text-red-500 p-8">{error}</div>;

  const { driver, docs = [], signed = [], insuranceCard, messages = [] } = data;
  const assignedVehicles = vehicles.filter((v) => String(v.assignedDriverId || "") === String(driver._id));
  const assignedCabNumbers = assignedVehicles.map((v) => v.cabNumber).filter(Boolean).join(", ");

  const enrollStatus = driver.enrollmentStatus || "not_started";
  const expiredDocs = docs.filter((d) => expiryStatus(d) === "expired").length;
  const expiringSoon = docs.filter((d) => expiryStatus(d) === "expiring_soon").length;

  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1";

  return (
    <div>
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel || "Delete"}
          confirmClass={confirm.confirmClass}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      {preview && (
        <DocumentPreviewModal
          link={preview.link}
          fileName={preview.fileName}
          mimeType={preview.mimeType}
          onClose={() => setPreview(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <button onClick={() => navigate("/enrollme/admin/drivers")} className="text-sm text-blue-600 hover:underline mb-2 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Drivers
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{driver.firstName} {driver.lastName}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{driver.email} · {driver.phone}</p>
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap justify-end">
          <button
            type="button"
            onClick={handleResendWelcomeEmail}
            disabled={resendingWelcome || !driver.email || !driver.phone}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
          >
            {resendingWelcome ? "Sending..." : "Resend portal login"}
          </button>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[driver.status] || STATUS_COLORS.pending}`}>
            {driver.status || "pending"}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${ENROLL_COLORS[enrollStatus]}`}>
            {enrollStatus.replace(/_/g, " ")}
          </span>
        </div>
      </div>

      {/* Alerts */}
      {expiredDocs > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 mb-3 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {expiredDocs} expired document{expiredDocs !== 1 ? "s" : ""} need attention.
        </div>
      )}
      {expiringSoon > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg px-4 py-2 mb-3 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {expiringSoon} document{expiringSoon !== 1 ? "s" : ""} expiring within 30 days.
        </div>
      )}

      {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-2 mb-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 border border-green-200 rounded-lg px-4 py-2 mb-3 text-sm">{success}</div>}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${tab === t ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
            {t}
            {t === "Documents" && expiredDocs > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{expiredDocs}</span>
            )}
            {t === "Signed Docs" && (
              <span className="ml-1.5 text-xs text-gray-400">{signed.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === "Overview" && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-semibold text-gray-900">Driver Information</h2>
              {!editMode && (
                <button onClick={() => setEditMode(true)} className="text-sm text-blue-600 hover:underline font-medium">Edit</button>
              )}
            </div>

            {editMode ? (
              <div className="space-y-4 max-w-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>First Name</label>
                    <input value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Last Name</label>
                    <input value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Phone (password)</label>
                  <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className={inputCls} />
                  <p className="text-xs text-gray-400 mt-1">Changing this resets the driver's login password.</p>
                </div>
                <div>
                  <label className={labelCls}>Account Status</label>
                  <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className={inputCls}>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="inactive">Inactive — blocks login</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Internal Notes</label>
                  <textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} className={inputCls} />
                </div>
                <div className="flex gap-3 pt-1">
                  <button onClick={handleSave} disabled={saving}
                    className="bg-blue-600 text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-60">
                    {saving ? "Saving…" : "Save Changes"}
                  </button>
                  <button onClick={() => setEditMode(false)} className="text-sm px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 max-w-lg">
                {[
                  ["First Name", driver.firstName],
                  ["Last Name", driver.lastName],
                  ["Email", driver.email],
                  ["Phone", driver.phone],
                  ["Cab #", assignedCabNumbers || "—"],
                  ["Account Status", driver.status],
                  ["Enrollment", enrollStatus.replace(/_/g, " ")],
                  ["Joined", new Date(driver.createdAt).toLocaleDateString()],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{k}</dt>
                    <dd className="text-sm text-gray-900 mt-0.5 capitalize">{v || "—"}</dd>
                  </div>
                ))}
                {driver.notes && (
                  <div className="col-span-2 border-t border-gray-100 pt-4 mt-1">
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</dt>
                    <dd className="text-sm text-gray-700">{driver.notes}</dd>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Enrollment checklist */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Enrollment Checklist</h2>
            {signed.length === 0 ? (
              <p className="text-sm text-gray-400">No documents signed yet.</p>
            ) : (
              <ul className="space-y-2">
                {signed.map((s) => (
                  <li key={s._id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      <span className="text-sm text-gray-900">{s.documentLabel}</span>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(s.signedAt).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {signed.length} of 5 required documents signed
              </span>
              <div className="w-40 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, (signed.length / 5) * 100)}%` }} />
              </div>
            </div>
          </div>

          {/* Verification letter */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4.5 h-4.5 text-blue-600" style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900 text-sm">Verification Letter</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Generate an independent contractor verification letter for {driver.firstName} {driver.lastName}.
                    Useful when a driver needs proof of their working arrangement for banks, housing, or other institutions.
                  </p>
                  {dlLetterError && <p className="text-xs text-red-600 mt-1">{dlLetterError}</p>}
                </div>
              </div>
              <button
                onClick={async () => {
                  setDlLetter(true);
                  setDlLetterError("");
                  try { await adminApi.downloadVerificationLetter(id); }
                  catch (e) { setDlLetterError(e.message || "Failed to generate letter."); }
                  finally { setDlLetter(false); }
                }}
                disabled={dlLetter}
                className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition whitespace-nowrap"
              >
                {dlLetter ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Generating…
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download PDF
                  </>
                )}
              </button>
            </div>
          </div>

          {/* AVI Letter Generator */}
          <div className="bg-white rounded-xl border border-gray-200">
            <button
              onClick={handleAviOpen}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 rounded-xl transition"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg style={{ width: 18, height: 18 }} fill="none" stroke="#d97706" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                      d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0M9 14l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900 text-sm">AVI Letter Generator</h2>
                  <p className="text-xs text-gray-500">Request tag, swap vehicle, or request refund — Denver International Airport</p>
                </div>
              </div>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${aviOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {aviOpen && (
              <div className="border-t border-gray-100 p-5 space-y-5">
                {/* Type selector */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Letter Type</p>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { key: "request_tag", label: "Request AVI Tag" },
                      { key: "swap",        label: "Swap Vehicle" },
                      { key: "refund",      label: "Request Refund" },
                    ].map(({ key, label: lbl }) => (
                      <button key={key} onClick={() => { setAviType(key); setAviError(""); }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                          aviType === key
                            ? "bg-amber-500 text-white border-amber-500"
                            : "bg-white text-gray-600 border-gray-300 hover:border-amber-400"
                        }`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Current vehicle fields */}
                {aviPrefilling ? (
                  <p className="text-xs text-gray-400">Loading vehicle data…</p>
                ) : (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {aviType === "swap" ? "Current Vehicle" : "Vehicle Information"}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        ["driverName",   "Driver Name",    "col-span-2"],
                        ["cabNumber",    "Cab #",          ""],
                        ["year",         "Year",           ""],
                        ["make",         "Make",           ""],
                        ["model",        "Model",          ""],
                        ["vin",          "VIN #",          "col-span-2"],
                        ["licensePlate", "License Plate",  "col-span-2"],
                      ].map(([field, label, span]) => (
                        <div key={field} className={span || ""}>
                          <label className="block text-xs text-gray-500 mb-1">{label}</label>
                          <input
                            value={aviForm[field]}
                            onChange={(e) => setAviForm({ ...aviForm, [field]: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Swap — new vehicle lookup */}
                {aviType === "swap" && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">New Vehicle</p>
                    <div className="flex gap-2 mb-3">
                      <input
                        placeholder="New Cab #"
                        value={aviLookupCab}
                        onChange={(e) => setAviLookupCab(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAviLookup()}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      />
                      <button
                        onClick={handleAviLookup}
                        disabled={aviLookingUp || !aviLookupCab.trim()}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition disabled:opacity-50"
                      >
                        {aviLookingUp ? "…" : "Look Up"}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        ["driverName",   "Driver Name",   "col-span-2"],
                        ["cabNumber",    "Cab #",         ""],
                        ["year",         "Year",          ""],
                        ["make",         "Make",          ""],
                        ["model",        "Model",         ""],
                        ["vin",          "VIN #",         "col-span-2"],
                        ["licensePlate", "License Plate", "col-span-2"],
                      ].map(([field, label, span]) => (
                        <div key={field} className={span || ""}>
                          <label className="block text-xs text-gray-500 mb-1">{label}</label>
                          <input
                            value={aviToForm[field]}
                            onChange={(e) => setAviToForm({ ...aviToForm, [field]: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {aviError && <p className="text-sm text-red-600">{aviError}</p>}

                <button
                  onClick={handleAviGenerate}
                  disabled={aviGenerating}
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition"
                >
                  {aviGenerating ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Generating…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download AVI Letter
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Danger zone */}
          <div className="bg-white rounded-xl border border-red-200 p-5">
            <h2 className="font-semibold text-red-700 mb-1">Danger Zone</h2>
            <p className="text-sm text-gray-500 mb-4">Permanently removes this driver and all associated data. This cannot be undone.</p>
            <button onClick={handleDeleteDriver} className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-5 py-2 rounded-lg transition">
              Delete Driver
            </button>
          </div>
        </div>
      )}

      {/* ── PROFILE ── */}
      {tab === "Profile" && (
        <div className="space-y-5">
          {/* Read view */}
          {!profileEditMode ? (
            <>
              {/* Personal */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">Personal Information</h2>
                  <button onClick={() => setProfileEditMode(true)} className="text-sm text-blue-600 hover:underline font-medium">Edit Profile</button>
                </div>
                {driver.profileCompleted
                  ? <span className="inline-block mb-3 text-xs bg-green-100 text-green-700 font-semibold px-2.5 py-1 rounded-full">Profile Complete</span>
                  : <span className="inline-block mb-3 text-xs bg-yellow-100 text-yellow-700 font-semibold px-2.5 py-1 rounded-full">Profile Incomplete</span>}
                <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    ["Date of Birth", driver.dateOfBirth ? new Date(driver.dateOfBirth).toLocaleDateString() : "—"],
                    ["Street", driver.address?.street || "—"],
                    ["City", driver.address?.city || "—"],
                    ["State", driver.address?.state || "—"],
                    ["ZIP", driver.address?.zip || "—"],
                  ].map(([k, v]) => (
                    <div key={k}><dt className={labelCls}>{k}</dt><dd className="text-sm text-gray-900 mt-0.5">{v}</dd></div>
                  ))}
                </dl>
              </div>

              {/* License */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="font-semibold text-gray-900 mb-4">Driver's License</h2>
                <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    ["License Number", driver.licenseNumber || "—"],
                    ["Issuing State", driver.licenseState || "—"],
                    ["Class", driver.licenseClass || "—"],
                    ["Expiry", driver.licenseExpiry ? new Date(driver.licenseExpiry).toLocaleDateString() : "—"],
                  ].map(([k, v]) => (
                    <div key={k}><dt className={labelCls}>{k}</dt><dd className="text-sm text-gray-900 mt-0.5">{v}</dd></div>
                  ))}
                </dl>
              </div>

              {/* DOT + CBI */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="font-semibold text-gray-900 mb-4">DOT Medical &amp; CBI Background Check</h2>
                <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    ["DOT Medical Cert #", driver.dotMedicalNumber || "—"],
                    ["DOT Medical Expiry", driver.dotMedicalExpiry ? new Date(driver.dotMedicalExpiry).toLocaleDateString() : "—"],
                    ["CBI Check Date", driver.cbiCheckDate ? new Date(driver.cbiCheckDate).toLocaleDateString() : "—"],
                    ["CBI Expiry Date", driver.cbiExpiryDate ? new Date(driver.cbiExpiryDate).toLocaleDateString() : "—"],
                  ].map(([k, v]) => (
                    <div key={k}><dt className={labelCls}>{k}</dt><dd className="text-sm text-gray-900 mt-0.5">{v}</dd></div>
                  ))}
                </dl>
              </div>

              {/* Business */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="font-semibold text-gray-900 mb-4">Business / Independent Contractor</h2>
                <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    ["LLC / Business Name", driver.llcName || "—"],
                    ["EIN", driver.ein || "—"],
                  ].map(([k, v]) => (
                    <div key={k}><dt className={labelCls}>{k}</dt><dd className="text-sm text-gray-900 mt-0.5">{v}</dd></div>
                  ))}
                </dl>
              </div>

              {/* Emergency */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="font-semibold text-gray-900 mb-4">Emergency Contact</h2>
                <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    ["Name", driver.emergencyContactName || "—"],
                    ["Phone", driver.emergencyContactPhone || "—"],
                    ["Relationship", driver.emergencyContactRelationship || "—"],
                  ].map(([k, v]) => (
                    <div key={k}><dt className={labelCls}>{k}</dt><dd className="text-sm text-gray-900 mt-0.5">{v}</dd></div>
                  ))}
                </dl>
              </div>
            </>
          ) : (
            /* Edit form */
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Edit Driver Profile</h2>
                <button onClick={() => setProfileEditMode(false)} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
              </div>

              {/* Personal */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Personal Information</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 grid grid-cols-2 gap-4">
                    <div><label className={labelCls}>Date of Birth</label>
                      <input type="date" value={profileForm.dateOfBirth} onChange={(e) => setProfileForm({ ...profileForm, dateOfBirth: e.target.value })} className={inputCls} /></div>
                  </div>
                  <div className="col-span-2"><label className={labelCls}>Street Address</label>
                    <input value={profileForm.address.street} onChange={(e) => setProfileForm({ ...profileForm, address: { ...profileForm.address, street: e.target.value } })} className={inputCls} /></div>
                  <div><label className={labelCls}>City</label>
                    <input value={profileForm.address.city} onChange={(e) => setProfileForm({ ...profileForm, address: { ...profileForm.address, city: e.target.value } })} className={inputCls} /></div>
                  <div><label className={labelCls}>ZIP</label>
                    <input value={profileForm.address.zip} onChange={(e) => setProfileForm({ ...profileForm, address: { ...profileForm.address, zip: e.target.value } })} className={inputCls} /></div>
                  <div><label className={labelCls}>State</label>
                    <select value={profileForm.address.state} onChange={(e) => setProfileForm({ ...profileForm, address: { ...profileForm.address, state: e.target.value } })} className={inputCls}>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select></div>
                </div>
              </div>

              {/* License */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Driver's License</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={labelCls}>License Number</label>
                    <input value={profileForm.licenseNumber} onChange={(e) => setProfileForm({ ...profileForm, licenseNumber: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>Issuing State</label>
                    <select value={profileForm.licenseState} onChange={(e) => setProfileForm({ ...profileForm, licenseState: e.target.value })} className={inputCls}>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select></div>
                  <div><label className={labelCls}>Class</label>
                    <select value={profileForm.licenseClass} onChange={(e) => setProfileForm({ ...profileForm, licenseClass: e.target.value })} className={inputCls}>
                      <option value="">—</option>
                      {["A","B","C","D","R"].map((c) => <option key={c} value={c}>Class {c}</option>)}
                    </select></div>
                  <div><label className={labelCls}>Expiry Date</label>
                    <input type="date" value={profileForm.licenseExpiry} onChange={(e) => setProfileForm({ ...profileForm, licenseExpiry: e.target.value })} className={inputCls} /></div>
                </div>
              </div>

              {/* DOT + CBI */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">DOT Medical &amp; CBI</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={labelCls}>DOT Medical Cert #</label>
                    <input value={profileForm.dotMedicalNumber} onChange={(e) => setProfileForm({ ...profileForm, dotMedicalNumber: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>DOT Medical Expiry</label>
                    <input type="date" value={profileForm.dotMedicalExpiry} onChange={(e) => setProfileForm({ ...profileForm, dotMedicalExpiry: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>CBI Check Date</label>
                    <input type="date" value={profileForm.cbiCheckDate} onChange={(e) => setProfileForm({ ...profileForm, cbiCheckDate: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>CBI Expiry Date</label>
                    <input type="date" value={profileForm.cbiExpiryDate} onChange={(e) => setProfileForm({ ...profileForm, cbiExpiryDate: e.target.value })} className={inputCls} /></div>
                </div>
              </div>

              {/* Business */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Business / IC Information</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={labelCls}>LLC / Business Name</label>
                    <input value={profileForm.llcName} onChange={(e) => setProfileForm({ ...profileForm, llcName: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>EIN</label>
                    <input value={profileForm.ein} onChange={(e) => setProfileForm({ ...profileForm, ein: e.target.value })} placeholder="XX-XXXXXXX" className={inputCls} /></div>
                </div>
              </div>

              {/* Emergency */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Emergency Contact</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className={labelCls}>Name</label>
                    <input value={profileForm.emergencyContactName} onChange={(e) => setProfileForm({ ...profileForm, emergencyContactName: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>Phone</label>
                    <input value={profileForm.emergencyContactPhone} onChange={(e) => setProfileForm({ ...profileForm, emergencyContactPhone: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>Relationship</label>
                    <select value={profileForm.emergencyContactRelationship} onChange={(e) => setProfileForm({ ...profileForm, emergencyContactRelationship: e.target.value })} className={inputCls}>
                      <option value="">—</option>
                      {["Spouse","Parent","Sibling","Child","Friend","Other"].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select></div>
                </div>
              </div>

              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button onClick={handleSaveProfile} disabled={savingProfile}
                  className="bg-blue-600 text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-60">
                  {savingProfile ? "Saving…" : "Save Profile"}
                </button>
                <button onClick={() => setProfileEditMode(false)} className="text-sm px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── DOCUMENTS ── */}
      {tab === "Documents" && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Compliance Documents</h2>
              <span className="text-xs text-gray-400">{docs.length} file{docs.length !== 1 ? "s" : ""}</span>
            </div>
            {docs.length === 0 ? (
              <p className="px-5 py-8 text-sm text-gray-400 text-center">No documents on file. Upload one below.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">Label</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Category</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Expiry</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Uploaded By</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Uploaded</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {docs.map((doc) => {
                    const status = expiryStatus(doc);
                    return (
                      <tr key={doc._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{doc.label}</td>
                        <td className="px-4 py-3 text-gray-500 capitalize">{doc.documentCategory?.replace(/_/g, " ")}</td>
                        <td className="px-4 py-3">
                          {doc.expiryDate ? (
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${EXPIRY_CHIP[status]}`}>
                              {status === "expired" ? "Expired" : status === "expiring_soon" ? "Expiring soon" : "Valid"} · {new Date(doc.expiryDate).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">No expiry</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500 capitalize">{doc.uploadedBy}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{new Date(doc.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-3 justify-end">
                            <button onClick={async () => { try { const res = await adminApi.getDocLink(doc._id); setPreview(res); } catch (e) { err(e.message); } }}
                              className="text-blue-600 hover:underline text-xs font-medium">View</button>
                            <button onClick={() => handleDeleteDoc(doc._id)} className="text-red-500 hover:underline text-xs font-medium">Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Upload New Document</h3>
            <form onSubmit={handleUploadDoc} className="space-y-3 max-w-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Category</label>
                  <select value={docForm.documentCategory} onChange={(e) => setDocForm({ ...docForm, documentCategory: e.target.value })} className={inputCls}>
                    {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Label</label>
                  <input value={docForm.label} onChange={(e) => setDocForm({ ...docForm, label: e.target.value })}
                    placeholder="e.g. Driver License 2026" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Expiry Date (if applicable)</label>
                <input type="date" value={docForm.expiryDate} onChange={(e) => setDocForm({ ...docForm, expiryDate: e.target.value })} className={inputCls} />
              </div>
              <FileUpload onFile={setDocFile} label="Select document" />
              <button type="submit" disabled={!docFile || uploadingDoc}
                className="bg-blue-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
                {uploadingDoc ? "Uploading…" : "Upload Document"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── SIGNED DOCS ── */}
      {tab === "Signed Docs" && (
        <SignedDocsTab signed={signed} driverId={id} />
      )}

      {/* ── INSURANCE CARD ── */}
      {tab === "Insurance Card" && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Current Insurance Card</h2>
            {insuranceCard ? (
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">{insuranceCard.fileName}</p>
                  {insuranceCard.expiryDate && (
                    <p className={`text-xs mt-0.5 ${new Date(insuranceCard.expiryDate) < new Date() ? "text-red-600 font-medium" : "text-gray-500"}`}>
                      Expires: {new Date(insuranceCard.expiryDate).toLocaleDateString()}
                      {new Date(insuranceCard.expiryDate) < new Date() && " — EXPIRED"}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">Uploaded: {new Date(insuranceCard.updatedAt || insuranceCard.createdAt).toLocaleDateString()}</p>
                  <div className="mt-3 flex items-center gap-4">
                    {insLink ? (
                      <a href={insLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline font-medium">Open file ↗</a>
                    ) : (
                      <button onClick={loadInsLink} className="text-sm text-blue-600 hover:underline font-medium">View / Download</button>
                    )}
                    <button
                      type="button"
                      onClick={handleDeleteInsuranceCard}
                      disabled={removingIns}
                      className="text-sm text-red-600 hover:text-red-700 hover:underline font-medium disabled:opacity-50"
                    >
                      {removingIns ? "Removing…" : "Remove Card"}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400">
                <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <p className="text-sm">No insurance card on file yet.</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-1">{insuranceCard ? "Replace Insurance Card" : "Upload Insurance Card"}</h2>
            <p className="text-sm text-gray-500 mb-4">Uploading a new card replaces the previous one. The driver will see it in their portal immediately.</p>
            <form onSubmit={handleUploadIns} className="space-y-3 max-w-sm">
              <div>
                <label className={labelCls}>Card Expiry Date</label>
                <input type="date" value={insExpiry} onChange={(e) => setInsExpiry(e.target.value)} className={inputCls} />
              </div>
              <FileUpload onFile={setInsFile} label="Select insurance card (PDF or image)" />
              <button type="submit" disabled={!insFile || uploadingIns}
                className="bg-blue-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 w-full">
                {uploadingIns ? "Uploading…" : insuranceCard ? "Replace Card" : "Upload Card"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── MESSAGES ── */}
      {tab === "Messages" && (
        <div className="space-y-6">
          {/* Compose */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-900 mb-4">Send Message to {driver.firstName}</h2>
            <form onSubmit={handleSendMessage} className="space-y-3 max-w-lg">
              <div>
                <label className={labelCls}>Subject</label>
                <input value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder="e.g. Document Reminder" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Message</label>
                <textarea value={composeBody} onChange={(e) => setComposeBody(e.target.value)} rows={4}
                  placeholder="Type your message…" className={`${inputCls} resize-none`} />
              </div>
              <button type="submit" disabled={!composeSubject.trim() || !composeBody.trim() || sendingMsg}
                className="bg-blue-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
                {sendingMsg ? "Sending…" : "Send Message"}
              </button>
            </form>
          </div>

          {/* History */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Message History</h2>
            </div>
            {messages.length === 0 ? (
              <p className="px-5 py-8 text-sm text-gray-400 text-center">No messages with this driver yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {messages.map((m) => (
                  <li key={m._id} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">{m.subject}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${m.senderType === "admin" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                            {m.senderType === "admin" ? "Admin" : "Driver"}
                          </span>
                          {m.isGlobal && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Broadcast</span>}
                        </div>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{m.body}</p>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">{new Date(m.createdAt).toLocaleDateString()}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
